// summon-cantrips-browser.ts — binds janus-engine/ability/summon-caster-cantrips.ts (Wizard v9 summons_gain_caster_cantrips, Dark
// Necromancy L30 "Death-Linked Familiar") to the page's caches: the controller row comes from casterCache (the realiased encounter member)
// else the cached encounter roster; definitions from the row's own catalog, the object cache, then the full-nest finder (every cache root).
// Used by the action-bar island / castable sheet (inject the controller's cantrips into the summon's spell list), the cast-legitimacy gate
// (ability-preconditions-browser.ts) and the legacy-route refusal (use-ability.ts fallback; ability-truth.ts reads the same engine module).
import { summonCantripCandidate, summonCantripLegacyMarker, summonControllerId, summonInjectedCantrips,
    type SummonCantripEntry, type SummonCantripReaders } from '../janus-engine/ability/summon-caster-cantrips';

type Row = Record<string, unknown>;
type PageWindow = Window & {
    FateTide?: { objectCache?: Map<string, unknown>; casterCache?: Map<string, unknown>; encounterCache?: Map<string, unknown> };
    ftFindFullNestedItem?: (id: string) => unknown;
};
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/;
const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const full = (value: unknown): Row | null => { const row = record(value); return row && record(row.items) && record(row.item_layer_types) ? row : null; };
const upper = (value: unknown): string => String((Array.isArray(value) ? value[0] : value) ?? '').trim().toUpperCase();

function entryId(entry: unknown): string {
    if (typeof entry === 'string') return upper(entry.split('|')[0]);
    const row = record(entry);
    return upper(String(row?.item_layer_effect ?? record(row?.items)?.item_id ?? row?.item_id ?? row?.status_item_id ?? '').split('|')[0]);
}

/** Page readers for the rule. `holder` lends its own catalog first (a member blob may carry none — the finder covers that). */
export function pageSummonCantripReaders(holder?: Row | null): SummonCantripReaders {
    const w = window as PageWindow;
    const resolveItem = (entry: unknown): Row | null => {
        const id = entryId(entry);
        if (!UUID.test(id)) return null;
        const catalog = record(holder?.catalog);
        const own = full(catalog?.[id] ?? catalog?.[id.toLowerCase()]);
        if (own) return own;
        const cached = full(w.FateTide?.objectCache?.get(id) ?? w.FateTide?.objectCache?.get(id.toLowerCase()));
        if (cached) return cached;
        try { return full(w.ftFindFullNestedItem?.(id) ?? null); } catch { return null; }
    };
    return {
        value: (row, key) => row ? ftLayerValue(row, key) ?? null : null,
        entries: (row, key) => row ? (ftLayerEntries(row, key) ?? []) : [],
        // creature_statuses() with each definition under status_arr (a cached row may carry only the catalog ref)
        statuses: row => (ftCreatureStatuses(row, 'target') ?? []).flatMap((raw: unknown) => {
            const status = record(raw);
            if (!status) return [];
            if (record(status.status_arr)) return [status];
            const definition = resolveItem(status.status_item_id ?? status.status_arr);
            return [definition ? { ...status, status_arr: definition } : status];
        }),
        resolveItem,
    };
}

/** The summon's controller row as the page holds it: casterCache (a full compile when the page fetched one), else the cached roster. */
export function pageControllerRow(summon: Row | null | undefined): Row | null {
    const id = summonControllerId(summon);
    if (!id) return null;
    const w = window as PageWindow;
    const cached = record(w.FateTide?.casterCache?.get(id) ?? w.FateTide?.casterCache?.get(id.toLowerCase()));
    if (cached) return cached;
    const encounterId = upper(record(summon?.encounter_creatures)?.encounter_id);
    const encounter = encounterId ? record(w.FateTide?.encounterCache?.get(encounterId) ?? w.FateTide?.encounterCache?.get(encounterId.toLowerCase())) : null;
    const roster = Array.isArray(encounter?.encounter_creatures_arr) ? encounter!.encounter_creatures_arr as unknown[] : [];
    for (const member of roster) {
        const row = record(member);
        if (row && upper(record(row.encounter_creatures)?.encounter_creature_id) === id) return row;
    }
    return null;
}

/** The controller's cantrips this summon may cast (empty for an ordinary creature, or a controller that does not provide the layer). */
export function pageSummonInjectedCantrips(summon: Row | null | undefined): SummonCantripEntry[] {
    if (!summon || !summonControllerId(summon)) return [];
    try {
        const controller = pageControllerRow(summon);
        return summonInjectedCantrips(pageSummonCantripReaders(controller), summon, controller);
    } catch (error) {
        console.debug('[summon-cantrips] list injection skipped', error);
        return [];
    }
}

/** The castGrantVerdict `controller` input: the held controller row, null when the summon has one the page does not hold, undefined
 *  for an ordinary creature (no controller → nothing to look up). */
export function pageCastGrantController(caster: Row | null | undefined): Row | null | undefined {
    if (!caster || !summonControllerId(caster)) return undefined;
    return pageControllerRow(caster);
}

/** The BROWSER-ONLY marker row for the legacy-route refusal (null = an ordinary use). */
export function pageSummonCantripLegacyMarker(caster: Row | null | undefined, ability: Row | null | undefined): Row | null {
    if (!caster || !ability || !summonControllerId(caster)) return null;
    try {
        const controller = pageControllerRow(caster);
        return summonCantripLegacyMarker(pageSummonCantripReaders(controller ?? caster), caster, controller, ability);
    } catch (error) {
        // an unreadable controller shape: refuse the legacy route only when the use IS a candidate (a level-0 spell the summon does not hold)
        // — PHP would resolve it with the summon's own stats; any other cast of a controlled creature keeps its route (Codex review 2026-10-09)
        console.debug('[summon-cantrips] legacy marker read failed', error);
        try {
            return summonCantripCandidate(pageSummonCantripReaders(caster), caster, ability)
                ? { item_layer_types: { summons_gain_caster_cantrips: 1 } } : null;
        } catch { return null; }
    }
}
